import type { DirectusQuery, LocalRow, QueryContext } from '../types/localDb.types'

/**
 * Emulasi subset query Directus REST (`/items/<collection>`) untuk local mode.
 * Mendukung: filter (operator umum + _and/_or + relasi M2O bertingkat), search, sort,
 * page/limit/offset, fields (termasuk `fk.field`, `fk.*`, alias O2M), dan aggregate.
 */

const DEFAULT_LIMIT = 100

type FilterObject = Record<string, unknown>

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const parseMaybeJson = <T>(value: unknown): T | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value !== 'string') return value as T
  try {
    return JSON.parse(value) as T
  } catch {
    return value as unknown as T
  }
}

const toList = (value: unknown): string[] => {
  if (value === undefined || value === null || value === '') return []
  if (Array.isArray(value)) return value.flatMap(item => toList(item))
  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

const resolveDynamic = (value: unknown, ctx: QueryContext): unknown => {
  if (value === '$CURRENT_USER') return ctx.userId
  if (value === '$NOW') return ctx.now.toISOString()
  if (Array.isArray(value)) return value.map(item => resolveDynamic(item, ctx))
  return value
}

const comparable = (value: unknown): number | string | null => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return value
  if (typeof value === 'boolean') return value ? 1 : 0
  const asNumber = Number(value)
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(asNumber) && !/^\d{4}-\d{2}-\d{2}/.test(value))
    return asNumber
  const asDate = Date.parse(String(value))
  if (!Number.isNaN(asDate) && /^\d{4}-\d{2}-\d{2}/.test(String(value))) return asDate
  return String(value)
}

const compare = (left: unknown, right: unknown): number => {
  const a = comparable(left)
  const b = comparable(right)
  if (a === null && b === null) return 0
  if (a === null) return -1
  if (b === null) return 1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'id', { numeric: true, sensitivity: 'base' })
}

const looselyEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) return true
  if (left === null || left === undefined || right === null || right === undefined) return false
  if (typeof left === 'boolean' || typeof right === 'boolean') return String(left) === String(right)
  return String(left) === String(right)
}

const OPERATORS: Record<string, (value: unknown, operand: unknown) => boolean> = {
  _eq: (value, operand) => looselyEqual(value, operand),
  _neq: (value, operand) => !looselyEqual(value, operand),
  _in: (value, operand) => toArray(operand).some(item => looselyEqual(value, item)),
  _nin: (value, operand) => !toArray(operand).some(item => looselyEqual(value, item)),
  _null: (value, operand) => (value === null || value === undefined) === isTruthy(operand),
  _nnull: (value, operand) => (value !== null && value !== undefined) === isTruthy(operand),
  _empty: (value, operand) => isEmptyValue(value) === isTruthy(operand),
  _nempty: (value, operand) => !isEmptyValue(value) === isTruthy(operand),
  _contains: (value, operand) => String(value ?? '').includes(String(operand)),
  _ncontains: (value, operand) => !String(value ?? '').includes(String(operand)),
  _icontains: (value, operand) =>
    String(value ?? '')
      .toLowerCase()
      .includes(String(operand).toLowerCase()),
  _starts_with: (value, operand) => String(value ?? '').startsWith(String(operand)),
  _istarts_with: (value, operand) =>
    String(value ?? '')
      .toLowerCase()
      .startsWith(String(operand).toLowerCase()),
  _ends_with: (value, operand) => String(value ?? '').endsWith(String(operand)),
  _gt: (value, operand) => value !== null && value !== undefined && compare(value, operand) > 0,
  _gte: (value, operand) => value !== null && value !== undefined && compare(value, operand) >= 0,
  _lt: (value, operand) => value !== null && value !== undefined && compare(value, operand) < 0,
  _lte: (value, operand) => value !== null && value !== undefined && compare(value, operand) <= 0,
  _between: (value, operand) => {
    const [from, to] = toArray(operand)
    return compare(value, from) >= 0 && compare(value, to) <= 0
  },
  _nbetween: (value, operand) => {
    const [from, to] = toArray(operand)
    return compare(value, from) < 0 || compare(value, to) > 0
  }
}

function toArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value
  if (typeof value === 'string') return value.split(',').map(item => item.trim())
  return [value]
}

function isTruthy(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1'
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value)) return value.length === 0
  return false
}

const isOperatorObject = (value: Record<string, unknown>) => Object.keys(value).some(key => key.startsWith('_'))

/** Evaluasi filter Directus terhadap satu row. */
export function matchFilter(
  row: LocalRow,
  filter: FilterObject | undefined,
  collection: string,
  ctx: QueryContext
): boolean {
  if (!filter || !isPlainObject(filter)) return true

  return Object.entries(filter).every(([key, condition]): boolean => {
    if (key === '_and')
      return toArray(condition).every((part): boolean => matchFilter(row, part as FilterObject, collection, ctx))
    if (key === '_or')
      return toArray(condition).some((part): boolean => matchFilter(row, part as FilterObject, collection, ctx))
    if (condition === undefined) return true

    const value = row[key]

    if (isPlainObject(condition) && !isOperatorObject(condition)) {
      const target = ctx.schema[collection]?.relations?.[key]
      if (target) {
        const related = ctx.resolve(target, isPlainObject(value) ? value.id : value)
        if (!related) return false
        return matchFilter(related, condition, target, ctx)
      }
      const alias = ctx.schema[collection]?.aliases?.[key]
      if (alias) {
        const children = ctx.children(alias.collection, alias.field, row.id)
        return children.some((child): boolean => matchFilter(child, condition, alias.collection, ctx))
      }
      return false
    }

    if (!isPlainObject(condition)) return looselyEqual(value, condition)

    return Object.entries(condition).every(([operator, operand]) => {
      const fn = OPERATORS[operator]
      if (!fn) return true
      const resolved = resolveDynamic(operand, ctx)
      const scalar = isPlainObject(value) ? value.id : value
      return fn(scalar, resolved)
    })
  })
}

function matchSearch(row: LocalRow, search: string, collection: string, ctx: QueryContext) {
  const term = search.trim().toLowerCase()
  if (!term) return true
  const configured = ctx.schema[collection]?.searchFields
  const keys = configured?.length ? configured : Object.keys(row).filter(key => typeof row[key] === 'string')
  return keys.some(key =>
    String(row[key] ?? '')
      .toLowerCase()
      .includes(term)
  )
}

function readPath(row: LocalRow, path: string, collection: string, ctx: QueryContext): unknown {
  const [head, ...rest] = path.split('.')
  const value = row[head]
  if (!rest.length) return value
  const target = ctx.schema[collection]?.relations?.[head]
  if (!target) return undefined
  const related = ctx.resolve(target, value)
  return related ? readPath(related, rest.join('.'), target, ctx) : undefined
}

function sortRows(rows: LocalRow[], sort: string[], collection: string, ctx: QueryContext) {
  if (!sort.length) return rows
  return [...rows].sort((a, b) => {
    for (const rawKey of sort) {
      const desc = rawKey.startsWith('-')
      const key = desc ? rawKey.slice(1) : rawKey
      const result = compare(readPath(a, key, collection, ctx), readPath(b, key, collection, ctx))
      if (result !== 0) return desc ? -result : result
    }
    return 0
  })
}

type FieldTree = { all: boolean; leaves: Set<string>; nested: Map<string, FieldTree> }

const emptyTree = (): FieldTree => ({ all: false, leaves: new Set(), nested: new Map() })

function buildFieldTree(fields: string[]): FieldTree {
  const tree = emptyTree()
  for (const field of fields.length ? fields : ['*']) {
    let node = tree
    const parts = field.split('.')
    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1
      if (isLast) {
        if (part === '*') node.all = true
        else node.leaves.add(part)
        return
      }
      if (!node.nested.has(part)) node.nested.set(part, emptyTree())
      node = node.nested.get(part)!
    })
  }
  return tree
}

function project(row: LocalRow, tree: FieldTree, collection: string, ctx: QueryContext, depth = 0): LocalRow {
  const output: Record<string, unknown> = {}
  if (tree.all) Object.assign(output, row)
  for (const leaf of tree.leaves) output[leaf] = row[leaf]
  if (depth > 4) return output as LocalRow

  for (const [key, child] of tree.nested) {
    const target = ctx.schema[collection]?.relations?.[key]
    if (target) {
      const related = ctx.resolve(target, row[key])
      output[key] = related ? project(related, child, target, ctx, depth + 1) : (row[key] ?? null)
      continue
    }
    const alias = ctx.schema[collection]?.aliases?.[key]
    if (alias) {
      output[key] = ctx
        .children(alias.collection, alias.field, row.id)
        .map(item => project(item, child, alias.collection, ctx, depth + 1))
      continue
    }
    output[key] = row[key] ?? null
  }

  if (!output.id && row.id && (tree.all || tree.leaves.size === 0)) output.id = row.id
  return output as LocalRow
}

function aggregate(rows: LocalRow[], spec: Record<string, string | string[]>) {
  const result: Record<string, unknown> = {}
  for (const [fn, rawFields] of Object.entries(spec)) {
    const fields = toList(rawFields)
    if (fn === 'count' && (fields.length === 0 || fields.includes('*'))) {
      result.count = rows.length
      continue
    }
    const bucket: Record<string, number | string | null> = {}
    for (const field of fields) {
      const values = rows.map(row => row[field]).filter(value => value !== null && value !== undefined)
      const numbers = values.map(Number).filter(value => !Number.isNaN(value))
      if (fn === 'count') bucket[field] = values.length
      else if (fn === 'countDistinct') bucket[field] = new Set(values.map(String)).size
      else if (fn === 'sum') bucket[field] = numbers.reduce((sum, value) => sum + value, 0)
      else if (fn === 'avg') bucket[field] = numbers.length ? numbers.reduce((s, v) => s + v, 0) / numbers.length : null
      else if (fn === 'min') bucket[field] = numbers.length ? Math.min(...numbers) : null
      else if (fn === 'max') bucket[field] = numbers.length ? Math.max(...numbers) : null
    }
    result[fn] = bucket
  }
  return result
}

/** Jalankan query Directus terhadap isi collection. Mengembalikan body response Directus. */
export function runQuery(collection: string, rows: LocalRow[], query: DirectusQuery, ctx: QueryContext) {
  const filter = parseMaybeJson<FilterObject>(query.filter)
  const search = typeof query.search === 'string' ? query.search : ''
  const filtered = rows.filter(
    row => matchFilter(row, filter, collection, ctx) && matchSearch(row, search, collection, ctx)
  )

  const aggregateSpec = parseMaybeJson<Record<string, string | string[]>>(query.aggregate)
  if (aggregateSpec && Object.keys(aggregateSpec).length) {
    const groupBy = toList(parseMaybeJson(query.groupBy))
    if (!groupBy.length) return { data: [aggregate(filtered, aggregateSpec)] }
    const groups = new Map<string, LocalRow[]>()
    for (const row of filtered) {
      const key = JSON.stringify(groupBy.map(field => row[field] ?? null))
      groups.set(key, [...(groups.get(key) ?? []), row])
    }
    return {
      data: [...groups.entries()].map(([key, groupRows]) => {
        const values = JSON.parse(key) as unknown[]
        const head = Object.fromEntries(groupBy.map((field, index) => [field, values[index]]))
        return { ...head, ...aggregate(groupRows, aggregateSpec) }
      })
    }
  }

  const sorted = sortRows(filtered, toList(parseMaybeJson(query.sort)), collection, ctx)
  const limit = query.limit === undefined || query.limit === '' ? DEFAULT_LIMIT : Number(query.limit)
  const page = query.page === undefined || query.page === '' ? undefined : Math.max(1, Number(query.page))
  const offset = page && limit > 0 ? (page - 1) * limit : Number(query.offset ?? 0)
  const paged = limit < 0 ? sorted.slice(offset) : sorted.slice(offset, offset + limit)
  const tree = buildFieldTree(toList(parseMaybeJson(query.fields)))
  const data = paged.map(row => project(row, tree, collection, ctx))

  const meta = String(query.meta ?? '')
  if (meta) {
    return {
      data,
      meta: {
        ...(meta.includes('filter_count') || meta === '*' ? { filter_count: filtered.length } : {}),
        ...(meta.includes('total_count') || meta === '*' ? { total_count: rows.length } : {})
      }
    }
  }
  return { data }
}

/** Proyeksi satu row (untuk GET by id / response mutation). */
export function projectRow(collection: string, row: LocalRow, fields: unknown, ctx: QueryContext) {
  return project(row, buildFieldTree(toList(parseMaybeJson(fields))), collection, ctx)
}
